import PropTypes from 'prop-types';
import { useState, useRef, useMemo, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import './TablaReporteUsuarios.scss';
import { Table, SelectTamanoPagina, Input } from 'components';
import { DATE_FORMAT_COLUMN_TABLE } from 'constants/dateFormat';
import { message } from 'antd';
import { downloadExcelReporteUsuarios } from 'actions/files.actions';
import { base64toBlob } from 'utils/file';
import { useDebounce } from 'hooks/useDebounce';
import { filterList } from 'utils/filterList';

export const TablaReporteUsuarios = ({
  reporteUsuariosData,
  loadingTable,
  onChangePagination,
  totalRegistros,
  paginationCurrent,
  paginationsPageSize
}) => {
  const filtersRef = useRef({});
  const [filters, setFilters] = useState({});
  const [downloadingExcel, setDownloadingExcel] = useState(true);
  const [messageApi, contextHolder] = message.useMessage();

  const showTotal = (total, range) => {
    return `Mostrando del ${range[0]} al ${range[1]} de ${total} registros`;
  };

  const [pagination, setPagination] = useState({
    position: ['bottomRight'],
    showQuickJumper: true,
    size: 'small',
    current: 1,
    pageSize: 10,
    showTotal,
    total: 0,
    showSizeChanger: false
  });

  const DEBOUNCE_TIME_FILTER = 2000;
  const applyFilter = useDebounce((fields) => setFilters(fields), DEBOUNCE_TIME_FILTER);
  const dispatch = useDispatch();

  const handleChangeFilter = ({ target }) => {
    const { value, name } = target;
    filtersRef.current[name] = value;
    applyFilter({ ...filtersRef.current });
  };

  const filter = (columnName, lblColumn) => (
    <label htmlFor={columnName}>
      {lblColumn}
      <Input
        id={columnName}
        placeholder="Filtrar"
        name={columnName}
        key={columnName}
        onChange={handleChangeFilter}
      />
    </label>
  );

  const filteredList = useMemo(() => {
    if (reporteUsuariosData !== null) {
      const { nombreUsuario, estatus } = reporteUsuariosData;
      const filteredData = reporteUsuariosData.filter((row) => {
        if (estatus && row?.idTipoMonitoreo !== estatus) return false;
        if (nombreUsuario !== undefined && nombreUsuario !== row?.nombreUsuario) return false;
        setDownloadingExcel(false);
        return true;
      });
      return filteredData;
    }
    setDownloadingExcel(true);
    return null;
  }, [reporteUsuariosData]);

  const dataList = useMemo(() => {
    return filterList(filteredList, filters);
  }, [filteredList, filters]);

  const columns = [
    {
      title: 'ID del usuario',
      dataIndex: 'idUsuario',
      width: '6%',
      key: 'idUsuario'
    },
    {
      title: filter('nombreUsuario', 'Nombre del usuario'),
      dataIndex: 'nombreUsuario',
      width: '7%',
      key: 'nombreUsuario'
    },
    {
      title: 'Rol',
      dataIndex: 'rol',
      width: '8%',
      key: 'rol'
    },
    {
      title: 'Fecha de alta',
      dataIndex: 'fechaAltaFormat',
      width: '10%',
      render: (d) => d.format(DATE_FORMAT_COLUMN_TABLE)
    },
    {
      title: filter('estatus', 'Estatus'),
      dataIndex: 'estatus',
      width: '8%',
      key: 'estatus'
    },
    {
      title: 'Correo electrónico',
      dataIndex: 'correoUsuario',
      width: '10%',
      key: 'correoUsuario'
    },
    {
      title: 'Número de empleado',
      dataIndex: 'numEmpleado',
      width: '6%',
      key: 'numEmpleado'
    },
    {
      title: 'Última modificación',
      dataIndex: 'ultimaModFormat',
      render: (d) => d.format(DATE_FORMAT_COLUMN_TABLE),
      width: '10%'
    },
    {
      title: 'Monitoreos asignados',
      dataIndex: 'monitoreos',
      width: '11%',
      key: 'monitoreos'
    },
    {
      title: 'Entidad',
      dataIndex: 'entidad',
      width: '20%',
      key: 'entidad'
    },
    {
      title: 'Área',
      dataIndex: 'area',
      width: '15%',
      key: 'area'
    },
    {
      title: 'Puesto',
      dataIndex: 'puesto',
      width: '15%',
      key: 'puesto'
    },
    {
      title: 'Sujeto Obligado',
      dataIndex: 'so',
      width: '15%',
      key: 'so'
    }
  ];

  const handleChangePageSize = (pageSize) => {
    setPagination((prevPagination) => ({ ...prevPagination, pageSize }));
    onChangePagination(pagination.current, pageSize);
  };

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    const { success, response } = await dispatch(
      downloadExcelReporteUsuarios({ reporteUsuarios: reporteUsuariosData })
    );
    if (response && success) {
      if (response.data.code === 200 && response.data.excel64) {
        const url = window.URL.createObjectURL(
          base64toBlob(response.data.excel64.base64, response.data.excel64.tipo)
        );
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', response.data.excel64.nombre);
        link.click();
      }
      console.log(messageApi);
    }
    setDownloadingExcel(false);
  };

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      total: totalRegistros
    }));
  }, [totalRegistros]);

  return (
    <div className="tabla-reporteUsuarios">
      <div className="table-repUsu-header">
        <div>
          <SelectTamanoPagina
            value={pagination.pageSize}
            onChange={handleChangePageSize}
            disableBottomExcel={downloadingExcel}
            onClick={handleDownloadExcel}
          />
        </div>
      </div>
      {contextHolder}
      <Table
        bordered={false}
        scroll={{ x: 2000 }}
        columns={columns}
        dataSource={dataList || reporteUsuariosData}
        loading={loadingTable}
        pagination={{
          ...pagination,
          onChange: (page, pageSize) => {
            paginationCurrent(page);
            paginationsPageSize(pageSize);
            setPagination((prev) => ({
              ...prev,
              current: page,
              pageSize: pageSize || prev.pageSize
            }));
          }
        }}
      />
    </div>
  );
};

TablaReporteUsuarios.propTypes = {
  reporteUsuariosData: PropTypes.arrayOf(
    PropTypes.shape({
      idUsuario: PropTypes.number.isRequired,
      nombreUsuario: PropTypes.string.isRequired,
      fechaAlta: PropTypes.string.isRequired,
      estatus: PropTypes.string.isRequired,
      correoUsuario: PropTypes.string.isRequired,
      numEmpleado: PropTypes.number.isRequired,
      ultimaMod: PropTypes.string.isRequired,
      monitoreos: PropTypes.string.isRequired,
      entidad: PropTypes.string.isRequired,
      puesto: PropTypes.string.isRequired
    })
  ).isRequired,
  loadingTable: PropTypes.bool.isRequired,
  onChangePagination: PropTypes.func.isRequired,
  totalRegistros: PropTypes.number.isRequired,
  paginationCurrent: PropTypes.func.isRequired,
  paginationsPageSize: PropTypes.func.isRequired
};
